import type { WebGLRGBA } from '@pn/services/color/webglColor';
import FRAG_SRC from '@pn/services/map/mapbox/custom/shaders/dots-polygon.frag.glsl?raw';
import VERT_SRC from '@pn/services/map/mapbox/custom/shaders/polygon.vert.glsl?raw';
import { MercatorCoordinate } from 'mapbox-gl';
import type { PatternModule } from '.';

type DotsPatternProps = {
  tileSize: number;
  dotRadius: number;
  color: WebGLRGBA;
};

export const DotsPatternModule: PatternModule<DotsPatternProps> = {
  vert: VERT_SRC,
  frag: FRAG_SRC,

  init(gl, program) {
    return {
      _uPhasePx: gl.getUniformLocation(program, 'u_phasePx'),
      _uTileSize: gl.getUniformLocation(program, 'u_tileSize'),
      _uRadius: gl.getUniformLocation(program, 'u_dotRadius'),
      _uColor: gl.getUniformLocation(program, 'u_dotColor'),
      _uDpr: gl.getUniformLocation(program, 'u_dpr'),
    };
  },

  setUniforms({ map, gl, dpr }, uLocs, props) {
    gl.uniform1f(uLocs._uTileSize, props.tileSize);
    gl.uniform1f(uLocs._uRadius, props.dotRadius);
    gl.uniform4fv(uLocs._uColor, props.color);
    gl.uniform1f(uLocs._uDpr, dpr);

    // phase logic
    const tileSize = Math.max(props.tileSize, 1);
    const zoom = map.getZoom();
    const worldSizePx = 512 * Math.pow(2, zoom);
    const topLeft = map.unproject([0, 0]);
    const w = MercatorCoordinate.fromLngLat(
      { lng: topLeft.lng, lat: topLeft.lat },
      0
    );

    const topLeftWorldPxX = w.x * worldSizePx;
    const topLeftWorldPxY = w.y * worldSizePx;

    const phaseX = ((topLeftWorldPxX % tileSize) + tileSize) % tileSize;
    const phaseY =
      (tileSize - (((topLeftWorldPxY % tileSize) + tileSize) % tileSize)) %
      tileSize;

    gl.uniform2f(uLocs._uPhasePx, phaseX, phaseY);
  },
};
